import * as React from 'react'
import clsx from 'clsx'
import { useControlled, useForkRef } from '../../hooks'
import { Menu } from '../Menu'
import { ownerDocument } from '../../utils/ownerDocument'
import { MenuProps } from '../Menu'
import { SxProps } from '../sx'
import { FormControlProps } from '../FormControl'
import { capitalize } from '../../utils'
import CaretDown from '../internal/svg-icons/CaretDown'
import { isFragment } from 'react-is'
import { isFilled } from '../utils'
import style from './index.module.scss'
import { Box } from '../Box'
import { Link } from '../Link'
import { Typography } from '../Typography'

export interface SelectClasses {
  /** Styles applied to the select component `select` class. */
  select: string
  /** Styles applied to the select component if `multiple={true}`. */
  multiple: string
  /** Styles applied to the select component if `variant="filled"`. */
  filled: string
  /** Styles applied to the select component if `variant="outlined"`. */
  outlined: string
  /** Styles applied to the select component if `variant="outlinedDash"`. */
  outlinedDash: string
  /** Styles applied to the select component if `variant="standard"`. */
  standard: string
  /** State class applied to the select component `disabled` class. */
  disabled: string
  /** State class applied to the select component if `readOnly={true}`. */
  readOnly: string
  /** Styles applied to the icon component. */
  icon: string
  /** Styles applied to the icon component if the popup is open. */
  iconOpen: string
  /** Styles applied to the icon component if `variant="filled"`. */
  iconFilled: string
  /** Styles applied to the icon component if `variant="outlined"`. */
  iconOutlined: string
  /** Styles applied to the icon component if `variant="standard"`. */
  iconStandard: string
  /** Styles applied to the underlying native input component. */
  nativeInput: string
  /** State class applied to the root element if `error={true}`. */
  error: string
}

/**
 * The change can be caused by different kind of events.
 * The type of event depends on what caused the change.
 * For example, when the browser auto-fills the `Select` you'll receive a `React.ChangeEvent`.
 */
export type SelectChangeEvent<T = string> =
  | (Event & { target: { value: T; name: string } })
  | React.ChangeEvent<HTMLInputElement>

export interface SelectInputProps<T = unknown>
  extends React.HTMLAttributes<HTMLInputElement> {
  autoFocus?: boolean
  autoWidth?: boolean
  defaultOpen?: boolean
  disabled?: boolean
  error?: boolean
  size?: 'small' | 'medium' | 'large'
  IconComponent?: React.ElementType
  inputRef?: React.Ref<any>
  MenuProps?: Partial<MenuProps>
  multiple?: boolean
  name?: string
  onClose?: (event: React.SyntheticEvent) => void
  onOpen?: (event: React.SyntheticEvent) => void
  open?: boolean
  readOnly?: boolean
  renderValue?: (value: T) => React.ReactNode
  SelectDisplayProps?: React.HTMLAttributes<HTMLDivElement>
  sx?: SxProps
  tabIndex?: number
  value?: T | ''
  variant?: FormControlProps['variant']
  labelId?: string
  displayEmpty?: boolean
  classes?: Partial<SelectClasses>
  type?: string
  /**
   * @ignore - internal props
   */
  renderContentTop?: JSX.Element
  /**
   * @ignore - internal props
   */
  renderContentBottom?: JSX.Element
  /**
   * @ignore - internal prop
   */
  hideSelectNav?: boolean
  /**
   * @ignore
   */
  placeholder: string
}

function areEqualValues(a: any, b: any) {
  if (typeof b === 'object' && b !== null) {
    return a === b
  }
  // The value could be a number, the DOM will stringify it anyway.
  return String(a) === String(b)
}

function isEmpty(display: any) {
  return display == null || (typeof display === 'string' && !display.trim())
}

/**
 * @ignore - internal component
 */

export const SelectInput = React.forwardRef<HTMLInputElement, SelectInputProps>(
  function SelectInput(props, ref) {
    const {
      'aria-describedby': ariaDescribedby,
      'aria-label': ariaLabel,
      autoFocus,
      autoWidth = false,
      children,
      className,
      defaultOpen,
      defaultValue,
      disabled,
      displayEmpty,
      error = false,
      IconComponent = CaretDown,
      inputRef: inputRefProp,
      labelId,
      MenuProps = {},
      multiple = false,
      name,
      onBlur,
      onChange,
      onClose,
      onFocus,
      onOpen,
      open: openProp,
      readOnly,
      renderValue,
      SelectDisplayProps = {},
      tabIndex: tabIndexProp,
      // catching `type` from Input which makes no sense for SelectInput
      type: _,
      value: valueProp,
      variant = 'standard',
      renderContentTop: renderContentTopProp,
      renderContentBottom,
      classes,
      placeholder,
      hideSelectNav = false,
      ...other
    } = props
    const [value, setValueState] = useControlled({
      controlled: valueProp,
      default: multiple
        ? Array.isArray(defaultValue)
          ? defaultValue
          : [defaultValue].filter(Boolean)
        : defaultValue,
      name: 'Select',
    })
    const [openState, setOpenState] = useControlled({
      controlled: openProp,
      default: defaultOpen,
      name: 'Select',
    })

    const inputRef = React.useRef(null)
    const displayRef = React.useRef<HTMLDivElement | null>(null)
    const [displayNode, setDisplayNode] = React.useState<HTMLDivElement | null>(
      null
    )
    const { current: isOpenControlled } = React.useRef(openProp != null)
    const [menuMinWidthState, setMenuMinWidthState] = React.useState<number>()
    const handleRef = useForkRef(ref, inputRefProp)
    const handleDisplayRef = React.useCallback<(node: HTMLDivElement) => void>(
      node => {
        displayRef.current = node
        if (node) {
          setDisplayNode(node)
        }
      },
      []
    )
    const anchorElement = displayNode?.parentNode as Element
    React.useImperativeHandle(
      handleRef,
      () => ({
        focus: () => {
          displayRef.current!.focus()
        },
        node: inputRef.current,
        value,
      }),
      [value]
    )
    // Resize menu on `defaultOpen` automatic toggle.
    React.useEffect(() => {
      if (defaultOpen && openState && displayNode && !isOpenControlled) {
        setMenuMinWidthState(autoWidth ? undefined : anchorElement!.clientWidth)
        displayRef.current!.focus()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayNode, autoWidth])
    // `isOpenControlled` is ignored because the component should never switch between controlled and uncontrolled modes.
    // `defaultOpen` and `openState` are ignored to avoid unnecessary callbacks.
    React.useEffect(() => {
      if (autoFocus) {
        displayRef.current!.focus()
      }
    }, [autoFocus])

    React.useEffect(() => {
      if (!labelId) {
        return undefined
      }
      const label = ownerDocument(displayRef.current).getElementById(labelId)
      if (label) {
        const handler = () => {
          if (getSelection()!.isCollapsed) {
            displayRef.current!.focus()
          }
        }
        label.addEventListener('click', handler)
        return () => {
          label.removeEventListener('click', handler)
        }
      }
      return undefined
    }, [labelId])

    const update = (open: boolean, event: React.UIEvent<HTMLDivElement>) => {
      if (open) {
        if (onOpen) {
          onOpen(event)
        }
      } else if (onClose) {
        onClose(event)
      }

      if (!isOpenControlled) {
        setMenuMinWidthState(autoWidth ? undefined : anchorElement.clientWidth)
        setOpenState(open)
      }
    }

    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
      // Ignore everything but left-click
      if (event.button !== 0) {
        return
      }
      // Hijack the default focus behavior.
      event.preventDefault()
      displayRef.current!.focus()

      update(true, event)
    }

    const handleClose = (event: React.UIEvent<HTMLDivElement>) => {
      update(false, event)
    }

    const childrenArray = React.Children.toArray(children)

    // Support autofill.
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const child = childrenArray.find(
        // @ts-ignore
        childItem => childItem.props.value === event.target.value
      )
      if (child === undefined) {
        return
      }
      // @ts-ignore
      setValueState(child.props.value)

      if (onChange) {
        // @ts-ignore
        onChange(event, child)
      }
    }

    const handleItemClick =
      (child: React.ReactElement) =>
      (event: React.MouseEvent<HTMLDivElement>) => {
        let newValue

        // We use the tabindex attribute to signal the available options.
        if (!event.currentTarget.hasAttribute('tabindex')) {
          return
        }

        if (multiple) {
          newValue = Array.isArray(value) ? value.slice() : []
          // @ts-ignore
          const itemIndex = value.indexOf(child.props.value)
          if (itemIndex === -1) {
            newValue.push(child.props.value)
          } else {
            newValue.splice(itemIndex, 1)
          }
        } else {
          newValue = child.props.value
        }

        if (child.props.onClick) {
          child.props.onClick(event)
        }

        if (value !== newValue) {
          setValueState(newValue)

          if (onChange) {
            // Redefine target to allow name and value to be read.
            // This allows seamless integration with the most popular form libraries.
            // https://github.com/mui/material-ui/issues/13485#issuecomment-676048492
            // Clone the event to not override `target` of the original event.
            const nativeEvent = event.nativeEvent || event
            // @ts-ignore
            const clonedEvent = new nativeEvent.constructor(
              nativeEvent.type,
              nativeEvent
            )

            Object.defineProperty(clonedEvent, 'target', {
              writable: true,
              value: { value: newValue, name },
            })
            // @ts-ignore
            onChange(clonedEvent, child)
          }
        }

        if (!multiple) {
          update(false, event)
        }
      }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!readOnly) {
        const validKeys = [
          ' ',
          'ArrowUp',
          'ArrowDown',
          // The native select doesn't respond to enter on macOS, but it's recommended by
          // https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/
          'Enter',
        ]

        if (validKeys.indexOf(event.key) !== -1) {
          event.preventDefault()
          update(true, event)
        }
      }
    }

    const open = displayNode !== null && openState

    const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
      // if open event.stopImmediatePropagation
      if (!open && onBlur) {
        // Preact support, target is read only property on a native event.
        Object.defineProperty(event, 'target', {
          writable: true,
          value: { value, name },
        })
        // @ts-ignore
        onBlur(event)
      }
    }

    delete other['aria-invalid']

    let display
    let displaySingle
    const displayMultiple: any[] = []
    let computeDisplay = false
    let foundMatch = false

    // No need to display any value if the field is empty.
    if (isFilled({ value }) || displayEmpty) {
      if (renderValue) {
        display = renderValue(value)
      } else {
        computeDisplay = true
      }
    }

    const items = childrenArray.map(child => {
      if (!React.isValidElement(child)) {
        return null
      }

      if (process.env.NODE_ENV !== 'production') {
        if (isFragment(child)) {
          console.error(
            [
              "The Select component doesn't accept a Fragment as a child.",
              'Consider providing an array instead.',
            ].join('\n')
          )
        }
      }

      let selected

      if (multiple) {
        if (!Array.isArray(value)) {
          console.error(
            [
              'The `value` prop must be an array ',
              'when using the `Select` component with `multiple`.',
            ].join('\n')
          )
        }
        // @ts-ignore
        selected = value.some(v => areEqualValues(v, child.props.value))
        if (selected && computeDisplay) {
          displayMultiple.push(child.props.children)
        }
      } else {
        selected = areEqualValues(value, child.props.value)
        if (selected && computeDisplay) {
          displaySingle = child.props.children
        }
      }

      if (selected) {
        foundMatch = true
      }

      return React.cloneElement(child, {
        // @ts-ignore
        'aria-selected': selected ? 'true' : 'false',
        onClick: handleItemClick(child),
        onKeyUp: (event: React.KeyboardEvent) => {
          if (event.key === ' ') {
            // otherwise our MenuItems dispatches a click event
            // it's not behavior of the native <option> and causes
            // the select to close immediately since we open on space keydown
            event.preventDefault()
          }

          if (child.props.onKeyUp) {
            child.props.onKeyUp(event)
          }
        },
        role: 'option',
        selected,
        value: undefined, // The value is most likely not a valid HTML attribute.
        'data-value': child.props.value, // Instead, we provide it as a data attribute.
      })
    })

    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      React.useEffect(() => {
        if (!foundMatch && !multiple && value !== '') {
          // @ts-ignore
          const values = childrenArray.map(child => child.props.value)
          console.warn(
            [
              `You have provided an out-of-range value \`${value}\` for the select ${
                name ? `(name="${name}") ` : ''
              }component.`,
              "Consider providing a value that matches one of the available options or ''.",
              `The available values are ${
                values
                  .filter(x => x != null)
                  .map(x => `\`${x}\``)
                  .join(', ') || '""'
              }.`,
            ].join('\n')
          )
        }
      }, [foundMatch, childrenArray, multiple, name, value])
    }

    const allValues = React.useMemo(
      // @ts-ignore
      () => childrenArray.map(child => child.props.value),
      [childrenArray]
    )

    const handleSelectAll = React.useCallback(
      (event: React.MouseEvent) => {
        setValueState(allValues)

        if (onChange) {
          // Redefine target to allow name and value to be read.
          // This allows seamless integration with the most popular form libraries.
          // https://github.com/mui/material-ui/issues/13485#issuecomment-676048492
          // Clone the event to not override `target` of the original event.
          const nativeEvent = event.nativeEvent || event
          // @ts-ignore
          const clonedEvent = new nativeEvent.constructor(
            nativeEvent.type,
            nativeEvent
          )

          Object.defineProperty(clonedEvent, 'target', {
            writable: true,
            value: { value: allValues, name },
          })
          // @ts-ignore
          onChange(clonedEvent)
        }
      },
      [allValues, setValueState, onChange, name]
    )

    const handleClearAll = React.useCallback(
      (event: React.MouseEvent) => {
        const emptyArray: any[] = []
        setValueState(emptyArray)

        if (onChange) {
          // Redefine target to allow name and value to be read.
          // This allows seamless integration with the most popular form libraries.
          // https://github.com/mui/material-ui/issues/13485#issuecomment-676048492
          // Clone the event to not override `target` of the original event.
          const nativeEvent = event.nativeEvent || event
          // @ts-ignore
          const clonedEvent = new nativeEvent.constructor(
            nativeEvent.type,
            nativeEvent
          )

          Object.defineProperty(clonedEvent, 'target', {
            writable: true,
            value: { value: emptyArray, name },
          })
          // @ts-ignore
          onChange(clonedEvent)
        }
      },
      [onChange, setValueState, name]
    )

    if (computeDisplay) {
      if (multiple) {
        if (displayMultiple.length === 0) {
          display = null
        } else {
          display = displayMultiple.reduce((output, child, index) => {
            output.push(child)
            if (index < displayMultiple.length - 1) {
              output.push(', ')
            }
            return output
          }, [])
        }
      } else {
        display = displaySingle
      }
    }

    // Avoid performing a layout computation in the render method.
    let menuMinWidth = menuMinWidthState

    if (!autoWidth && isOpenControlled && displayNode) {
      menuMinWidth = anchorElement.clientWidth
    }

    let tabIndex
    if (typeof tabIndexProp !== 'undefined') {
      tabIndex = tabIndexProp
    } else {
      tabIndex = disabled ? undefined : 0
    }

    const buttonId =
      SelectDisplayProps.id ||
      (name ? `factor-component-select-${name}` : undefined)
    const renderContentTop = (
      <Box className={style['renderContent--top']}>
        {renderContentTopProp && renderContentTopProp}
        {!hideSelectNav && multiple ? (
          <Box className={style.selectNav}>
            <Link
              Component={'button'}
              color="primary"
              sx={{ textWeight: 'demi' }}
              onClick={handleSelectAll}
              disabled={
                Array.isArray(value) && value.length === allValues.length
              }
            >
              Select All
            </Link>
            <Link
              Component={'button'}
              color="primary"
              sx={{ textWeight: 'demi' }}
              onClick={handleClearAll}
              disabled={Array.isArray(value) && value.length === 0}
            >
              Clear All
            </Link>
          </Box>
        ) : null}
      </Box>
    )

    return (
      <React.Fragment>
        <div
          ref={handleDisplayRef}
          tabIndex={tabIndex}
          role="button"
          aria-disabled={disabled ? 'true' : undefined}
          aria-expanded={open ? 'true' : 'false'}
          aria-haspopup="listbox"
          aria-label={ariaLabel}
          aria-labelledby={
            [labelId, buttonId].filter(Boolean).join(' ') || undefined
          }
          aria-describedby={ariaDescribedby}
          onKeyDown={handleKeyDown}
          onMouseDown={disabled || readOnly ? undefined : handleMouseDown}
          onBlur={handleBlur}
          onFocus={onFocus}
          {...SelectDisplayProps}
          className={clsx(
            [style.select, classes?.select],
            disabled && [style.disabled, classes?.disabled],
            readOnly && [style.readOnly, classes?.readOnly],
            style[`variant${capitalize(variant)}`],
            classes?.[variant],
            error && [classes?.error],
            open && style.focused,
            SelectDisplayProps.className,
            className
          )}
          // The id is required for proper a11y
          id={buttonId}
        >
          {/* So the vertical align positioning algorithm kicks in. */}
          {isEmpty(display) ? (
            // notranslate needed while Google Translate will not fix zero-width space issue
            <>
              <span className="notranslate">&#8203;</span>
              <Typography className={style.placeholder}>
                {placeholder}
              </Typography>
            </>
          ) : (
            display
          )}
        </div>
        <input
          aria-invalid={error}
          // @ts-ignore
          value={Array.isArray(value) ? value.join(',') : value}
          name={name}
          ref={inputRef}
          aria-hidden
          onChange={handleChange}
          tabIndex={-1}
          disabled={disabled}
          className={clsx(classes?.nativeInput, style['selectNativeInput'])}
          /*eslint-disable-next-line */
          autoFocus={autoFocus}
          {...other}
        />
        <IconComponent
          className={clsx(
            [style.selectIcon, classes?.icon],
            disabled && style.disabled,
            open && [style.open, classes?.iconOpen],
            style[`variant${capitalize(variant)}`],
            // @ts-ignore
            classes?.[`icon${capitalize(variant)}`]
          )}
        />
        <Menu
          /* eslint-disable-next-line */
          autoFocus={false}
          transitionDuration={0}
          id={`menu-${name || ''}`}
          anchorEl={anchorElement}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          {...MenuProps}
          MenuListProps={{
            'aria-labelledby': labelId,
            role: 'listbox',
            disableListWrap: true,
            className: style.menuList,
            disabledItemsFocusable: true,
            ...MenuProps.MenuListProps,
          }}
          PaperProps={{
            className: style.menuWrapper,
            elevation: 0,
            ...MenuProps.PaperProps,
            style: {
              marginTop: 2,
              minWidth: menuMinWidth,
              ...(MenuProps.PaperProps != null
                ? MenuProps.PaperProps.style
                : null),
            },
          }}
          renderContentTop={renderContentTop}
          renderContentBottom={renderContentBottom}
        >
          {items}
        </Menu>
      </React.Fragment>
    )
  }
)
